import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { NavActions } from '../components/NavBar/NavActions';
import { MobileUnsupported } from '../components/MobileUnsupported';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard } from '../components/StatCard';
import { ArrowUp, MoreVertical, Plus, Sprout, AlertTriangle, Trophy, Wine, Workflow, Pencil, Copy, Clock, Pause, Play, Trash2 } from 'lucide-react';
interface AutomationRule {
  id: string;
  name: string;
  category: 'Lifecycle' | 'Behaviour' | 'Value' | 'Product' | 'Seasonal';
  tagLabel: string;
  tagColor: 'green' | 'red' | 'indigo' | 'blue' | 'orange' | 'grey';
  membersMatched: number;
  lastTriggered: string;
  active: boolean;
}
const INITIAL_RULES: AutomationRule[] = [{
  id: '1',
  name: 'First-Time Member',
  category: 'Lifecycle',
  tagLabel: 'New Customer',
  tagColor: 'green',
  membersMatched: 24,
  lastTriggered: '2 hours ago',
  active: true
}, {
  id: '2',
  name: 'At-Risk Member',
  category: 'Behaviour',
  tagLabel: 'Needs Attention',
  tagColor: 'red',
  membersMatched: 18,
  lastTriggered: '1 hour ago',
  active: true
}, {
  id: '3',
  name: 'Loyal Member (12+ months)',
  category: 'Lifecycle',
  tagLabel: 'Loyal',
  tagColor: 'indigo',
  membersMatched: 156,
  lastTriggered: 'Yesterday',
  active: true
}, {
  id: '4',
  name: 'High Value Spender',
  category: 'Value',
  tagLabel: 'High Value',
  tagColor: 'blue',
  membersMatched: 42,
  lastTriggered: '3 hours ago',
  active: true
}, {
  id: '5',
  name: 'Red Wine Lover',
  category: 'Product',
  tagLabel: 'Premium',
  tagColor: 'orange',
  membersMatched: 89,
  lastTriggered: 'Yesterday',
  active: true
}, {
  id: '6',
  name: 'Engagement Drop-off',
  category: 'Behaviour',
  tagLabel: 'Needs Attention',
  tagColor: 'red',
  membersMatched: 31,
  lastTriggered: '4 hours ago',
  active: true
}, {
  id: '7',
  name: 'Anniversary Approaching',
  category: 'Seasonal',
  tagLabel: 'VIP',
  tagColor: 'red',
  membersMatched: 8,
  lastTriggered: 'Today',
  active: true
}, {
  id: '8',
  name: 'Summer Pauser',
  category: 'Seasonal',
  tagLabel: 'Seasonal',
  tagColor: 'blue',
  membersMatched: 0,
  lastTriggered: 'Never',
  active: true
}, {
  id: '9',
  name: 'Wine Explorer',
  category: 'Product',
  tagLabel: 'Premium',
  tagColor: 'orange',
  membersMatched: 67,
  lastTriggered: 'Yesterday',
  active: true
}, {
  id: '10',
  name: 'Corporate Gifter',
  category: 'Value',
  tagLabel: 'Corporate',
  tagColor: 'grey',
  membersMatched: 12,
  lastTriggered: '2 weeks ago',
  active: false
}, {
  id: '11',
  name: 'Sparkling Fan',
  category: 'Product',
  tagLabel: 'Premium',
  tagColor: 'orange',
  membersMatched: 23,
  lastTriggered: '1 week ago',
  active: false
}, {
  id: '12',
  name: 'Plan Downgrader',
  category: 'Value',
  tagLabel: 'Needs Attention',
  tagColor: 'red',
  membersMatched: 5,
  lastTriggered: '3 weeks ago',
  active: false
}];
const TEMPLATES = [{
  icon: Sprout,
  name: 'New Member Welcome',
  description: 'Tag first-time subscribers and remove after second order.'
}, {
  icon: AlertTriangle,
  name: 'At-Risk Detection',
  description: 'Flag members who skip 2+ orders in 3 months.'
}, {
  icon: Trophy,
  name: 'Loyalty Recognition',
  description: 'Tag members who reach 12-month and 24-month milestones.'
}, {
  icon: Wine,
  name: 'Varietal Preferences',
  description: 'Auto-tag based on red, white, or mixed wine selections.'
}];
export function AutomationRulesPage() {
  const navigate = useNavigate();
  const [rules, setRules] = useState<AutomationRule[]>(INITIAL_RULES);
  const [activeTab, setActiveTab] = useState<'All Rules' | 'Active' | 'Paused'>('All Rules');
  // Menu State
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);
  // Click outside handler for menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeMenuId && !(event.target as Element).closest('.rule-menu-container')) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenuId]);
  const activeCount = rules.filter(r => r.active).length;
  const pausedCount = rules.filter(r => !r.active).length;
  const filteredRules = rules.filter(rule => {
    if (activeTab === 'Active') return rule.active;
    if (activeTab === 'Paused') return !rule.active;
    return true;
  });
  const toggleRule = (id: string) => {
    setRules(prev => prev.map(rule => rule.id === id ? {
      ...rule,
      active: !rule.active
    } : rule));
  };
  const handleDuplicate = (rule: AutomationRule) => {
    setActiveMenuId(null);
    navigate('/settings/automation-rules/create', {
      state: {
        duplicatedRuleName: `${rule.name} - copy`
      }
    });
  };
  const handleDelete = () => {
    if (deleteRuleId) {
      setRules(rules.filter(r => r.id !== deleteRuleId));
      setDeleteRuleId(null);
    }
  };
  const getCategoryStyles = (category: string) => {
    switch (category) {
      case 'Lifecycle':
        return 'bg-[#DBEAFE] text-[#1E40AF]';
      case 'Behaviour':
        return 'bg-[#F3E8FF] text-[#7C3AED]';
      case 'Value':
        return 'bg-[#FEF3C7] text-[#B45309]';
      case 'Product':
        return 'bg-[#CCFBF1] text-[#0F766E]';
      case 'Seasonal':
        return 'bg-[#FCE7F3] text-[#BE185D]';
      default:
        return 'bg-[#F1F5F9] text-[#475569]';
    }
  };
  const getTagStyles = (color: string) => {
    switch (color) {
      case 'green':
        return 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]';
      case 'red':
        return 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]';
      case 'indigo':
        return 'bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]';
      case 'blue':
        return 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]';
      case 'orange':
        return 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]';
      case 'grey':
        return 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]';
      default:
        return 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]';
    }
  };
  return <>
      <div className="block md:hidden" data-id="element-4742">
        <MobileUnsupported data-id="element-4743" />
      </div>

      <div className="hidden md:block bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-4744">
        {/* Header Section */}
        <div className="mb-6" data-id="element-4745">
          <nav className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-4746">
            <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-4747">
              Automation Rules
            </h1>
            <div aria-hidden="true" className="flex-1" data-id="element-4748"></div>
            <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-4750" />} onClick={() => navigate('/settings/automation-rules/create')} data-id="element-4749">
              Create Rule
            </Button>
            <NavActions data-id="element-4751" />
          </nav>
        </div>

        {/* Subtitle */}
        <p className="text-sm text-[#64748B] mb-6" data-id="element-4752">
          Automate tagging based on member behaviour, lifecycle events, and
          subscription activity.
        </p>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-4753">
          <StatCard label="Active Rules" value={activeCount} trend={<div className="flex items-center gap-1.5" data-id="element-4755">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" data-id="element-4756" />
                <span className="text-xs text-[#10B981] font-medium" data-id="element-4757">Live</span>
              </div>} data-id="element-4754" />
          <StatCard label="Paused Rules" value={pausedCount} trend={<div className="flex items-center gap-1.5" data-id="element-4759">
                <div className="w-2 h-2 rounded-full bg-[#94A3B8]" data-id="element-4760" />
                <span className="text-xs text-[#94A3B8] font-medium" data-id="element-4761">
                  Paused
                </span>
              </div>} data-id="element-4758" />
          <StatCard label="Members Tagged Today" value="28" trend={{
          value: '+12%',
          isPositive: true
        }} data-id="element-4762" />
          <StatCard label="Total Auto-Tags Applied" value="1,847" secondaryValue="Last 30 days" data-id="element-4763" />
        </div>

        {/* Tabs */}
        <div className="border-b border-[#E2E8F0] mb-6" data-id="element-4764">
          <div className="flex gap-8" data-id="element-4765">
            <button onClick={() => setActiveTab('All Rules')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'All Rules' ? 'text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-4766">
              All Rules ({rules.length})
              {activeTab === 'All Rules' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9]" data-id="element-4767" />}
            </button>
            <button onClick={() => setActiveTab('Active')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'Active' ? 'text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-4768">
              Active ({activeCount})
              {activeTab === 'Active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9]" data-id="element-4769" />}
            </button>
            <button onClick={() => setActiveTab('Paused')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'Paused' ? 'text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-4770">
              Paused ({pausedCount})
              {activeTab === 'Paused' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9]" data-id="element-4771" />}
            </button>
          </div>
        </div>

        {/* Rules List */}
        <div className="space-y-3 mb-10" data-id="element-4772">
          {filteredRules.map(rule => <div key={rule.id} onClick={() => navigate(`/settings/automation-rules/${rule.id}`)} className={`bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center gap-4 transition-all cursor-pointer hover:border-[#0EA5E9] hover:shadow-sm ${!rule.active ? 'opacity-60' : ''}`} data-id="element-4773">
              {/* Toggle Switch */}
              <button onClick={e => {
            e.stopPropagation();
            toggleRule(rule.id);
          }} className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${rule.active ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-4774">
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${rule.active ? 'left-6' : 'left-1'}`} data-id="element-4775" />
              </button>

              {/* Rule Name */}
              <div className="w-64 flex-shrink-0" data-id="element-4776">
                <span className="text-sm font-semibold text-[#0F172A] group-hover:text-[#0EA5E9]" data-id="element-4777">
                  {rule.name}
                </span>
              </div>

              {/* Category Badge */}
              <div className="w-32 flex-shrink-0" data-id="element-4778">
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${getCategoryStyles(rule.category)}`} data-id="element-4779">
                  {rule.category}
                </span>
              </div>

              {/* Tag Pill */}
              <div className="w-40 flex-shrink-0" data-id="element-4780">
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${getTagStyles(rule.tagColor)}`} data-id="element-4781">
                  {rule.tagLabel}
                </span>
              </div>

              {/* Members Matched */}
              <div className="flex-1 text-xs text-[#94A3B8]" data-id="element-4782">
                {rule.membersMatched} members matched
              </div>

              {/* Right Side Info */}
              <div className="flex items-center gap-4 flex-shrink-0 relative rule-menu-container" data-id="element-4783">
                <span className="text-xs text-[#94A3B8]" data-id="element-4784">
                  Last triggered: {rule.lastTriggered}
                </span>
                <button onClick={e => {
              e.stopPropagation();
              setActiveMenuId(activeMenuId === rule.id ? null : rule.id);
            }} className={`p-1 rounded-md transition-colors ${activeMenuId === rule.id ? 'bg-[#F1F5F9] text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#64748B] hover:bg-[#F1F5F9]'}`} data-id="element-4785">
                  <MoreVertical className="w-4 h-4" data-id="element-4786" />
                </button>

                {/* Dropdown Menu */}
                {activeMenuId === rule.id && <div className="absolute right-0 top-8 w-56 bg-white rounded-lg shadow-lg border border-[#E2E8F0] py-1 z-20 animate-in fade-in zoom-in-95 duration-100" data-id="element-4787">
                    <button onClick={e => {
                e.stopPropagation();
                navigate(`/settings/automation-rules/${rule.id}`);
              }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-4788">
                      <Pencil className="w-4 h-4 text-[#64748B]" data-id="element-4789" />
                      Edit Rule
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                handleDuplicate(rule);
              }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-4790">
                      <Copy className="w-4 h-4 text-[#64748B]" data-id="element-4791" />
                      Duplicate Rule
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                const ruleName = encodeURIComponent(rule.name);
                setActiveMenuId(null);
                navigate(`/operations/activity-log?rule=${ruleName}`);
              }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-4792">
                      <Clock className="w-4 h-4 text-[#64748B]" data-id="element-4793" />
                      View History
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                toggleRule(rule.id);
                setActiveMenuId(null);
              }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-4794">
                      {rule.active ? <>
                          <Pause className="w-4 h-4 text-[#64748B]" data-id="element-4795" />
                          Pause Rule
                        </> : <>
                          <Play className="w-4 h-4 text-[#64748B]" data-id="element-4796" />
                          Resume Rule
                        </>}
                    </button>
                    <div className="h-px bg-[#E2E8F0] my-1" data-id="element-4797" />
                    <button onClick={e => {
                e.stopPropagation();
                setDeleteRuleId(rule.id);
                setActiveMenuId(null);
              }} className="w-full text-left px-4 py-2.5 text-sm text-[#EF4444] hover:bg-[#FEF2F2] flex items-center gap-2.5 transition-colors" data-id="element-4798">
                      <Trash2 className="w-4 h-4" data-id="element-4799" />
                      Delete Rule
                    </button>
                  </div>}
              </div>
            </div>)}
        </div>

        {/* Templates Section */}
        <div data-id="element-4800">
          <div className="mb-4" data-id="element-4801">
            <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-4802">Templates</h2>
            <p className="text-sm text-[#64748B]" data-id="element-4803">
              Start with a pre-built rule and customise it to your winery.
            </p>
          </div>

          <div className="flex overflow-x-auto gap-4 pb-4 -mx-1 px-1" data-id="element-4804">
            {TEMPLATES.map(template => <div key={template.name} className="bg-white border border-[#E2E8F0] rounded-lg p-5 min-w-[260px] w-[260px] flex flex-col flex-shrink-0 hover:border-[#CBD5E1] transition-colors" data-id="element-4805">
                <template.icon className="w-8 h-8 text-[#0EA5E9] mb-3" data-id="element-4806" />
                <h3 className="text-sm font-semibold text-[#0F172A] mb-1" data-id="element-4807">
                  {template.name}
                </h3>
                <p className="text-xs text-[#64748B] mb-4 flex-1" data-id="element-4808">
                  {template.description}
                </p>
                <Button variant="ghost" size="sm" onClick={() => navigate('/settings/automation-rules/create')} data-id="element-4809">
                  Use Template
                </Button>
              </div>)}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteRuleId && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4810">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity animate-in fade-in duration-200" onClick={() => setDeleteRuleId(null)} data-id="element-4811" />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200 scale-100" data-id="element-4812">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mb-4 mx-auto" data-id="element-4813">
                <Trash2 className="w-6 h-6 text-[#EF4444]" data-id="element-4814" />
              </div>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-2 text-center" data-id="element-4815">
                Delete this rule?
              </h3>

              <p className="text-sm text-[#64748B] mb-6 leading-relaxed text-center" data-id="element-4816">
                Any tags applied by this rule will remain on members but will no
                longer be automatically managed. This cannot be undone.
              </p>

              <div className="flex gap-3" data-id="element-4817">
                <Button variant="secondary" className="flex-1 justify-center" onClick={() => setDeleteRuleId(null)} data-id="element-4818">
                  Cancel
                </Button>
                <Button variant="danger" className="flex-1 justify-center" onClick={handleDelete} data-id="element-4819">
                  Delete
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </>;
}